import { useCallback } from 'react'
import {
  ApiGetWithToken,
	ApiPostWithToken,
	useApiFetch,
	useApiPost,
	useApiPostList
} from 'shared/api'
import {
	sharedListApi,
	sharedListApiBy
} from 'interface/endpoints'
import {
	SharedListResult,
	SharedListForm,
	AddSharedListFormData,
	AddSharedListResult,
	SharedListUploadResult
} from './types'

export * from './types'

export const useGetSharedList = (
	sharedListId: number | null | undefined
) => useApiFetch(
	useCallback(
		(apiAccess, cancelToken) => {
			if (!sharedListId) return null
      return ApiGetWithToken<SharedListResult>(
        apiAccess,
        sharedListApiBy(sharedListId),
        cancelToken
      )
    },
		[sharedListId]
	)
)

export const usePostSharedList = (
  callback?: {
    onPosted?: (result: SharedListUploadResult) => void
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError?: (reason: any) => void
  }
) => {
	const [uploadState, startUpload] = useUploadSharedList({
		onError: callback?.onError,
		onUploaded: callback?.onPosted
	})
	const [postListState, startListPost] = usePostAddSharedList({
		onError: callback?.onError,
		onPosted: (sharedList, postData, hash) => startUpload({ sharedList, postData, hash })
	})
	const [postState, post] = useApiPost<{
		form: SharedListForm,
		dataForm: AddSharedListFormData,
		hash: string
	}, SharedListResult>(
		useCallback(
			(apiAccess, cancelToken, { form }, webSocketId) => {
				return ApiPostWithToken(
					apiAccess,
					sharedListApi(),
					form,
					cancelToken,
					webSocketId
				)
			},
			[]
		),
		useCallback(
			(result, { dataForm, hash }) => {
				startListPost({
					sharedList: result,
					form: dataForm,
					hash
				}, dataForm.personalIdList.length, 1000)
				return result
			},
			[startListPost]
		),
		useCallback(
			(reason) => {
				if (callback?.onError) callback?.onError(reason)
			},
			[callback]
		)
	)
	return {
		post,
		postState,
		postListState,
		uploadState
	}
}

const usePostAddSharedList = (
  callback?: {
    onPosted?: (
			sharedList: SharedListResult, 
			postData: AddSharedListResult,
			hash: string
		) => void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError?: (reason: any) => void
  }
) => useApiPostList<{
	sharedList: SharedListResult, 
	form: AddSharedListFormData,
	hash: string
}, AddSharedListResult>(
	useCallback(
		(apiAccess, { sharedList, form }, from, to, cancelToken, webSocketId) => {
			return ApiPostWithToken(
				apiAccess,
        sharedListApiBy(sharedList.sharedListId, '/add'),
				{ personalIdList: form.personalIdList.slice(from, to) },
				cancelToken,
				webSocketId
			)
		},
		[]
	),
	useCallback(
		(results, { sharedList, hash }) => {
			const summary: AddSharedListResult = {
				recordCount: 0
			}
			for(const result of results) {
				summary.recordCount += result.recordCount
			}
			if (callback?.onPosted) callback.onPosted(sharedList, summary, hash)
			return summary
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

const useUploadSharedList = (
  callback?: {
    onUploaded?: (result: SharedListUploadResult) => void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError?: (reason: any) => void
  }
) => useApiPost<{
	sharedList: SharedListResult,
	postData: AddSharedListResult,
	hash: string
}, SharedListResult>(
	useCallback(
		(apiAccess, cancelToken, { sharedList }, webSocketId) => {
			return ApiPostWithToken(
				apiAccess,
        sharedListApiBy(sharedList.sharedListId, '/upload'),
				undefined,
				cancelToken,
				webSocketId
			)
		},
		[]
	),
	useCallback(
		(result, { sharedList, postData, hash }) => {
			if (callback?.onUploaded) callback.onUploaded({ sharedList, postData, hash })
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)
